import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, interval } from 'rxjs';
import { environment } from 'src/app/environments/environments';
import { GeofenceResponse } from '../state-management/states/guard-patrol.state';
import { LocationService } from './location/location.service';
import { ToastrService } from 'ngx-toastr';
import { IncidentReportingService } from './incident/incident-reporting.service';

export interface LocationCheckpoint {
  latitude: number;
  longitude: number;
  timestamp: Date;
  isInsideZone: boolean;
  distanceFromCenter?: number;
  violationReported: boolean;
}

export interface MonitoringSummary {
  totalChecks: number;
  inZoneChecks: number;
  outZoneChecks: number;
  violationCount: number;
  complianceRate: number;
  startTime: Date;
  endTime: Date | null;
  totalDuration: number;
}

export interface MonitoringData {
  sessionId: string;
  geofenceId: string;
  checkpoints: LocationCheckpoint[];
  summary: MonitoringSummary;
}

export interface ViolationReport {
  sessionId: string;
  geofenceId: string;
  latitude: number;
  longitude: number;
  timestamp: Date;
  distanceFromZone: number;
  isFirstViolation: boolean;
}

interface StoredMonitoringState {
  sessionId: string;
  geofenceId: string;
  checkpoints: LocationCheckpoint[];
  hasReportedViolation: boolean;
  monitoringStartTime: string;
}

@Injectable({
  providedIn: 'root',
})
export class PatrolMonitoringService implements OnDestroy {
  private readonly MONITORING_INTERVAL = environment.MONITORING_INTERVAL;
  private readonly API_BASE_URL = 'https://your-api-url.com/api';
  private readonly STORAGE_KEY = 'patrol_monitoring_state';

  private isMonitoring = false;
  private currentSessionId: string | null = null;
  private currentGeofence: GeofenceResponse | null = null;
  private monitoringSubscription?: Subscription;
  private checkpointsSubject = new BehaviorSubject<LocationCheckpoint[]>([]);
  private checkpoints: LocationCheckpoint[] = [];
  private hasReportedViolation = false;
  private monitoringStartTime: Date | null = null;
  private isMonitoringSubject = new BehaviorSubject<boolean>(false);

  private isReportingViolation = false;
  private isSendingCheckpoints = false;
  private isPerformingLocationCheck = false;

  constructor(
    private locationService: LocationService,
    private http: HttpClient,
    private messageService: ToastrService,
    private incidentService: IncidentReportingService
  ) {
    this.loadStateFromStorage();
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
  }

  /**
   * Load monitoring state from localStorage
   */
  private loadStateFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return;

      const state: StoredMonitoringState = JSON.parse(stored);

      // Restore checkpoints with Date objects
      this.checkpoints = state.checkpoints.map((cp) => ({
        ...cp,
        timestamp: new Date(cp.timestamp),
      }));

      this.hasReportedViolation = state.hasReportedViolation;
      this.monitoringStartTime = new Date(state.monitoringStartTime);
      this.currentSessionId = state.sessionId;

      this.checkpointsSubject.next([...this.checkpoints]);

      console.log('Monitoring state restored from storage', {
        checkpoints: this.checkpoints.length,
        sessionId: state.sessionId,
      });
    } catch (error) {
      console.error('Error loading monitoring state:', error);
      this.clearStoredState();
    }
  }

  /**
   * Save monitoring state to localStorage
   */
  private saveStateToStorage(): void {
    if (!this.currentSessionId || !this.isMonitoring) return;

    try {
      const state: StoredMonitoringState = {
        sessionId: this.currentSessionId,
        geofenceId: this.currentGeofence!.id,
        checkpoints: this.checkpoints,
        hasReportedViolation: this.hasReportedViolation,
        monitoringStartTime: this.monitoringStartTime!.toISOString(),
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving monitoring state:', error);
    }
  }

  /**
   * Clear stored state
   */
  private clearStoredState(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing stored state:', error);
    }
  }

  /**
   * Resume monitoring from stored state
   */
  resumeMonitoring(sessionId: string, geofence: GeofenceResponse): boolean {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored) return false;

    try {
      const state: StoredMonitoringState = JSON.parse(stored);

      if (state.sessionId !== sessionId) {
        this.clearStoredState();
        return false;
      }

      this.currentSessionId = sessionId;
      this.currentGeofence = geofence;
      this.isMonitoring = true;
      this.isMonitoringSubject.next(true);

      console.log('Resuming patrol monitoring...', {
        sessionId,
        existingCheckpoints: this.checkpoints.length,
      });

      this.monitoringSubscription = interval(
        this.MONITORING_INTERVAL
      ).subscribe(() => {
        this.performLocationCheck();
      });

      this.performLocationCheck();
      return true;
    } catch (error) {
      console.error('Error resuming monitoring:', error);
      this.clearStoredState();
      return false;
    }
  }

  /**
   * Start monitoring the patrol location
   */
  startMonitoring(sessionId: string, geofence: GeofenceResponse): void {
    if (this.isMonitoring && this.currentSessionId === sessionId) {
      console.warn('Monitoring already active for this session');
      return;
    }

    // Try to resume from stored state first
    if (this.resumeMonitoring(sessionId, geofence)) {
      return;
    }

    this.currentSessionId = sessionId;
    this.currentGeofence = geofence;
    this.isMonitoring = true;
    this.isMonitoringSubject.next(true);
    this.checkpoints = [];
    this.hasReportedViolation = false;
    this.monitoringStartTime = new Date();
    this.checkpointsSubject.next([]);

    console.log('Starting patrol monitoring...', {
      sessionId,
      geofenceId: geofence.id,
      interval: this.MONITORING_INTERVAL,
    });

    this.monitoringSubscription = interval(this.MONITORING_INTERVAL).subscribe(
      () => {
        this.performLocationCheck();
      }
    );

    this.performLocationCheck();
  }

  /**
   * Stop monitoring and return all collected data
   */
  async stopAndGetData(): Promise<MonitoringData> {
    if (!this.isMonitoring) {
      throw new Error('Monitoring is not active');
    }

    await this.performLocationCheck();

    const monitoringData: MonitoringData = {
      sessionId: this.currentSessionId!,
      geofenceId: this.currentGeofence!.id,
      checkpoints: [...this.checkpoints],
      summary: this.generateSummary(),
    };

    // await this.sendCheckpointsToBackend(monitoringData);

    this.stopMonitoring();
    this.clearStoredState();

    return monitoringData;
  }

  /**
   * Stop monitoring without returning data
   */
  stopMonitoring(): void {
    if (this.monitoringSubscription) {
      this.monitoringSubscription.unsubscribe();
      this.monitoringSubscription = undefined;
    }

    this.isMonitoring = false;
    this.isMonitoringSubject.next(false);
    this.currentSessionId = null;
    this.currentGeofence = null;
    this.hasReportedViolation = false;
    this.monitoringStartTime = null;

    this.isReportingViolation = false;
    this.isSendingCheckpoints = false;
    this.isPerformingLocationCheck = false;

    console.log('Patrol monitoring stopped');
  }

  /**
   * Get observable of checkpoints
   */
  getCheckpoints(): Observable<LocationCheckpoint[]> {
    return this.checkpointsSubject.asObservable();
  }

  /**
   * Get observable of monitoring status
   */
  getMonitoringStatus(): Observable<boolean> {
    return this.isMonitoringSubject.asObservable();
  }

  /**
   * Get current monitoring status
   */
  isActive(): boolean {
    return this.isMonitoring;
  }

  /**
   * Get current session ID
   */
  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Get monitoring summary
   */
  getCurrentSummary(): MonitoringSummary | null {
    if (!this.isMonitoring) return null;
    return this.generateSummary();
  }

  /**
   * Perform a location check
   */
  private async performLocationCheck(): Promise<void> {
    if (this.isPerformingLocationCheck) {
      console.log('Location check already in progress, skipping...');
      return;
    }

    if (!this.isMonitoring || !this.currentGeofence) {
      return;
    }
    this.isPerformingLocationCheck = true;
    try {
      const locationData = await this.locationService
        .getCurrentLocation(false)
        .toPromise();

      if (!locationData) {
        console.error('Failed to get location data');
        return;
      }

      const userLocation = new google.maps.LatLng(
        locationData.latitude,
        locationData.longitude
      );
      const isInsideZone = this.isPointInsideGeofence(
        userLocation,
        this.currentGeofence
      );

      let distanceFromCenter: number | undefined;
      if (this.currentGeofence.type === 'circle') {
        const center = new google.maps.LatLng(
          this.currentGeofence.centerLatitude!,
          this.currentGeofence.centerLongitude!
        );
        distanceFromCenter =
          google.maps.geometry.spherical.computeDistanceBetween(
            userLocation,
            center
          );
      }

      const checkpoint: LocationCheckpoint = {
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        timestamp: new Date(),
        isInsideZone,
        distanceFromCenter,
        violationReported: false,
      };

      if (!isInsideZone) {
        await this.handleViolation(checkpoint);
      }

      this.checkpoints.push(checkpoint);
      this.checkpointsSubject.next([...this.checkpoints]);
      this.saveStateToStorage();

      console.log('Location check performed:', {
        isInsideZone,
        checkpointCount: this.checkpoints.length,
        distanceFromCenter,
      });
    } catch (error) {
      console.error('Error performing location check:', error);
    } finally {
      this.isPerformingLocationCheck = false;
    }
  }

  /**
   * Handle violation when guard goes outside geofence
   */
  private async handleViolation(checkpoint: LocationCheckpoint): Promise<void> {
    if (this.hasReportedViolation) {
      console.log('Violation detected but already reported for this session');
      return;
    }

    if (this.isReportingViolation) {
      console.log('Violation reporting already in progress, skipping...');
      return;
    }
    this.isReportingViolation = true;
    try {
      const distanceFromZone = this.calculateDistanceToGeofence(
        new google.maps.LatLng(checkpoint.latitude, checkpoint.longitude),
        this.currentGeofence!
      );

      const violationReport: ViolationReport = {
        sessionId: this.currentSessionId!,
        geofenceId: this.currentGeofence!.id,
        latitude: checkpoint.latitude,
        longitude: checkpoint.longitude,
        timestamp: checkpoint.timestamp,
        distanceFromZone,
        isFirstViolation: true,
      };

      await this.reportViolationToBackend(violationReport);

      checkpoint.violationReported = true;
      this.hasReportedViolation = true;
      this.saveStateToStorage();

      this.messageService.warning(
        `⚠️ You are outside the patrol zone! Distance: ${distanceFromZone.toFixed(
          0
        )}m`,
        'Patrolling',
        { timeOut: 5000 }
      );

      console.log('Violation reported:', violationReport);
    } catch (error) {
      console.error('Error handling violation:', error);
      this.messageService.error('Failed to report violation');

      this.isReportingViolation = false;
    } finally {
      if (this.hasReportedViolation) {
        this.isReportingViolation = false;
      }
    }
  }

  /**
   * Check if a point is inside the geofence
   */
  private isPointInsideGeofence(
    point: google.maps.LatLng,
    geofence: GeofenceResponse
  ): boolean {
    if (geofence.type === 'circle') {
      const center = new google.maps.LatLng(
        geofence.centerLatitude!,
        geofence.centerLongitude!
      );
      const distance = google.maps.geometry.spherical.computeDistanceBetween(
        point,
        center
      );
      return distance <= geofence.radiusMeters!;
    } else if (geofence.type === 'polygon' && geofence.polygonVertices) {
      const polygonPath = [...geofence.polygonVertices]
        .sort((a, b) => a.order - b.order)
        .map((v) => new google.maps.LatLng(v.latitude, v.longitude));

      const polygon = new google.maps.Polygon({ paths: polygonPath });
      return google.maps.geometry.poly.containsLocation(point, polygon);
    }

    return false;
  }

  /**
   * Calculate distance from point to geofence boundary
   */
  private calculateDistanceToGeofence(
    point: google.maps.LatLng,
    geofence: GeofenceResponse
  ): number {
    if (geofence.type === 'circle') {
      const center = new google.maps.LatLng(
        geofence.centerLatitude!,
        geofence.centerLongitude!
      );
      const distance = google.maps.geometry.spherical.computeDistanceBetween(
        point,
        center
      );
      return Math.max(0, distance - geofence.radiusMeters!);
    }

    return 0;
  }

  /**
   * Generate monitoring summary
   */
  private generateSummary(): MonitoringSummary {
    const inZoneChecks = this.checkpoints.filter((c) => c.isInsideZone).length;
    const outZoneChecks = this.checkpoints.filter(
      (c) => !c.isInsideZone
    ).length;
    const violationCount = this.checkpoints.filter(
      (c) => c.violationReported
    ).length;
    const complianceRate =
      this.checkpoints.length > 0
        ? (inZoneChecks / this.checkpoints.length) * 100
        : 100;

    const endTime = new Date();
    const totalDuration = this.monitoringStartTime
      ? (endTime.getTime() - this.monitoringStartTime.getTime()) / 1000
      : 0;

    return {
      totalChecks: this.checkpoints.length,
      inZoneChecks,
      outZoneChecks,
      violationCount,
      complianceRate,
      startTime: this.monitoringStartTime!,
      endTime,
      totalDuration,
    };
  }

  /**
   * Report violation to backend
   */
  private async reportViolationToBackend(
    violation: ViolationReport
  ): Promise<void> {
    try {
      // Get username
      const username = this.incidentService.getCurrentUser().firstName;

      // Create incident for the violation
      const incidentResponse = await this.incidentService
        .raiseIncident({
          incidentName: 'Unmonitored logs/activity',
          incidentDescription: `${username} is reported out of the assigned geofence zone`,
          priority: 'high',
        })
        .toPromise();

      if (incidentResponse.success) {
        console.log(
          'Violation incident created successfully:',
          incidentResponse.data
        );
      } else {
        console.error(
          'Failed to create violation incident:',
          incidentResponse.error
        );
        throw new Error('Failed to report violation');
      }
    } catch (error) {
      console.error('Error reporting violation:', error);
      throw error;
    }
  }

  /**
   * Send all checkpoint data to backend when patrol stops
   */
  private async sendCheckpointsToBackend(
    monitoringData: MonitoringData
  ): Promise<void> {
    if (this.isSendingCheckpoints) {
      console.log('Checkpoints already being sent, skipping...');
      return;
    }
    this.isSendingCheckpoints = true;
    try {
      const response = await this.http
        .post(`${this.API_BASE_URL}/patrol/checkpoints`, monitoringData)
        .toPromise();

      console.log('Checkpoints sent to backend:', response);
    } catch (error) {
      console.error('Error sending checkpoints to backend:', error);
      throw error;
    } finally {
      this.isSendingCheckpoints = false;
    }
  }
}
